import * as models from "app/models/requireme";
import { createSelectorRx, search, nullsafe, assertUnreachable, mapMany, exclude, excludeKeys, leftJoin, union, unionKey, first, intersect, coalesce, intersectKeys, sum, any, contains, innerJoin, firstMap } from "keautils";



/**Devuelve true si se puede finalizar una entrega, o una cadena explicando la razon de porque no se puede */
export function puedeFinalizar(entrega: models.EntregaZERealizadaDTO, cliente: models.ClienteCreditoDto): true | "faltaMotivo" | "faltaContacto" | "saldoInsuficiente" | "creditoBloqueado" | "sinGuias" {

    if (entrega.IdContactoRecibio == null && (entrega.NombreContactoRecibio == null || entrega.NombreContactoRecibio == "")) return "faltaContacto";
    const noEntregadas = entrega.GuiasNoEntregadas.map(x => x.MotivoNoEntrega.Descripcion)
    const faltaMotivo = any(noEntregadas, x => !x);
    if (faltaMotivo) return "faltaMotivo";

    //Solo verificamos el limite de credito si el tipo de pago es crédito, por que es posible que el cliente se pase del limite de crédito con otros pagos pero aún asi
    //nos debe de permitir pagar con efectivo o cheque
    if (entrega.TipoPago == models.TipoPagoEntregaZE.Credito) {
        //Simulamos la finalización y vemos si el saldo del cliente supera al limite de crédito del cliente

        const guias = entrega.Guias;
        const idGuiasEntregadas = entrega.GuiasEntregadas.map(x => x.IdGuia);
        const guiasEntregadasCredito = guias.filter(x => x.FletePorCobrar && idGuiasEntregadas.indexOf(x.IdRegistro) != -1);
        const totalGuiasEntregadasCredito = sum(guiasEntregadasCredito.map(x => x.Importes.Total));
        const saldo = cliente.SaldoCredito + totalGuiasEntregadasCredito;

        if (saldo > (cliente.LimiteCredito || 0))
            return "saldoInsuficiente";

        if (cliente.CreditoBloqueado)
            return "creditoBloqueado";
    }

    const cantGuias = entrega.GuiasNoEntregadas.length + entrega.GuiasEntregadas.length;
    if (cantGuias == 0) return "sinGuias";

    return true;
}

/**Obtiene el total a pagar de una entrega, el campo "todas" es el total de todas las guias que se deben de entregar en esta entrega,
 * y el campo "entregadas" es el importe solo de las guias que si estan marcadas como entregadas
 */
export function obtenerTotalAPagar(entrega: models.EntregaZERealizadaDTO): {
    todas: number,
    entregadas: number
} {
    //Las guias que se van a mostrar son todas las guias de ese destino, menos las guias que ya fueron incluidas en otras entregas de ese mismo destino
    const guias = entrega.Guias;
    const guiasACobrar = guias.filter(x => x.FletePorCobrar);

    //Obtemos esta entrega y las otras entregas de la misma direccion
    const idGuiasEntregadas = entrega.GuiasEntregadas.map(x => x.IdGuia);
    const guiasAPagar = intersectKeys(guiasACobrar, idGuiasEntregadas, x => x.IdRegistro);

    const importeEntregadas = sum(guiasAPagar.map(x => x.Importes.Total));
    const importeTodas = sum(guiasACobrar.map(x => x.Importes.Total));
    return {
        todas: importeTodas,
        entregadas: importeEntregadas
    };
}

/**Obtiene la lista de guias que se va a mostrar en una entrega, dada la clave de la entrega y el state actual */
export function obtenerGuiasDeState(entrega: models.EntregaZERealizadaDTO): models.GuiaEntregaZESelect[] {

    //Las guias que se van a mostrar son todas las guias de ese destino, menos las guias que ya fueron incluidas en otras entregas de ese mismo destino
    //const guias = obtenerGuiasEntrega(state, claveEntrega);
    //const guias = entrega.Guias.map(x => x.Guia);
    const guias = entrega.Guias;

    type GuiaEntregaDatos = {
        idGuia: number;
        entregada: boolean;
        motivo: string
    };

    //Datos de las guias segun la entrega actual, incluye todas las guias que ya fueron entregadas o rechazadas;

    const guiasEntregaDatos: GuiaEntregaDatos[] = [
        ...entrega.GuiasNoEntregadas.map<GuiaEntregaDatos>(x => ({
            entregada: false,
            idGuia: x.IdGuia,
            motivo: x.MotivoNoEntrega.Descripcion
        })),
        ...entrega.GuiasEntregadas.map<GuiaEntregaDatos>(x => ({
            entregada: true,
            idGuia: x.IdGuia,
            motivo: ""
        }))
    ]

    //Por cada guia incluida en esta entrega tenemos que obtener el motivo y si esta seleccionada o no.
    const guiasEntrega = leftJoin(guias, guiasEntregaDatos, (guia, datos) => guia.IdRegistro == datos.idGuia).map<models.GuiaEntregaZESelect>(x => ({
        Guia: x.left,
        Seleccionada: coalesce(nullsafe(x.right, x => x.entregada), null),
        MotivoRechazo: nullsafe(x.right, x => x.motivo) || "",
    }));

    return guiasEntrega;
}

interface geolocation {
    lat: number | null;
    lng: number | null;
}

function getLocation(): Promise<geolocation> {
    var promise = new Promise(function (resolve, reject) {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
                },
                function (error) {
                    if (error.code == error.PERMISSION_DENIED)
                        console.log("Error al obtener ubicación: no se han concedido los permisos para obtener la ubicación.");
                    else
                        if (error.code == error.POSITION_UNAVAILABLE)
                            console.log("Error al obtener ubicación: posición invalida.");
                        else
                            if (error.code == error.TIMEOUT)
                                console.log("Error al obtener ubicación: tiempo agotado.");
                    console.log("Mensaje: " + error.message);
                    resolve({ lat: null, lng: null });
                });
        } else {
            reject({ lat: null, lng: null });
        }
    }) as Promise<geolocation>;
    return promise;
}

/**Marca como finalizada una entrega */
export async function onFinalizarEntrega(entrega: models.EntregaZERealizadaDTO, firma: models.FirmaModel): Promise<models.EntregaZERealizadaDTO> {
    let latitude: number | null = null;
    let longitude: number | null = null;

    var location = await getLocation();
    latitude = location.lat;
    longitude = location.lng;

    const entregaModificada = {
        ...entrega,
        Finalizado: true,
        FirmaUsuario: firma,
        PosicionLat: latitude,
        PosicionLng: longitude,
        Fotos: firma.Foto ? [firma.Foto] : []
    };

    return entregaModificada;
};


/**Devuelve un nuevo state al aplicar el cambio de seleccionar una guia ya sea para entrega, rechazo o excluida */
export function onSeleccionarGuiaEntregaChange(entrega: models.EntregaZERealizadaDTO, idGuia: number, value: boolean | null): models.EntregaZERealizadaDTO {
    //Si value es null, se excluye tanto de las no entregas como de las entregadas,
    //Si value es false, se excluye de las entregadas pero se incluye en las no entregadas
    //Si value es true, se excluye de las no entregadas pero se incluye en las entregadas
    const newValue: models.EntregaZERealizadaDTO = {
        ...entrega,
        GuiasNoEntregadas:
            value == false ?
                [...entrega.GuiasNoEntregadas, { IdGuia: idGuia, MotivoNoEntrega: { Descripcion: "" } } as models.GuiaNoEntregadaZE] :
                entrega.GuiasNoEntregadas.filter(x => x.IdGuia != idGuia),
        GuiasEntregadas:
            value == true ?
                union(entrega.GuiasEntregadas, [{ IdGuia: idGuia } as models.GuiaEntregadaZE]) :
                entrega.GuiasEntregadas.filter(x => x.IdGuia != idGuia)
    };
    return newValue;
}

export function onMotivoGuiaEntregaChange(entrega: models.EntregaZERealizadaDTO, idGuia: number, value: string): models.EntregaZERealizadaDTO {
    const newValue: models.EntregaZERealizadaDTO = {
        ...entrega,
        GuiasNoEntregadas: entrega.GuiasNoEntregadas.map(x => x.IdGuia == idGuia ? { ...x, MotivoNoEntrega: { Descripcion: value } } as models.GuiaNoEntregadaZE : x)
    };
    return newValue;
}

export interface SaldoCliente {
    saldo: number;
    limite: number;
}



// WEBPACK FOOTER //
// ./app/features/general/operativa/items/viajeappZE/entrega/logic.ts